import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Docs.css';

function Docs() {
  const [activeSection, setActiveSection] = useState('quickstart');
  const [copiedId, setCopiedId] = useState(null);
  const [navExpanded, setNavExpanded] = useState(false);

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const quickStartCode = `using IQLedger.Sdk;

var client = new IQLedgerClient(
    apiBase: "https://api.iqledger.io",
    credential: "<your-api-key>"  // API key from Profile page (format: keyId.secret)
);

// Log an LLM interaction
client.Log(
    prompt: "What is the capital of France?",
    response: "The capital of France is Paris."
);

// Ensure logs are sent before app exits
await client.FlushAsync();`;

  const batchingCode = `var options = new SdkOptions
{
    MaxQueueSize = 1000,
    Batching = new BatchingSettings
    {
        Enabled = true,
        MaxBatchSize = 10,           // Default: 10
        FlushInterval = TimeSpan.FromSeconds(2)  // Default: 2s
    }
};

var client = new IQLedgerClient(apiBase, credential, options: options);`;

  const offlineCode = `var options = new SdkOptions
{
    OfflineQueue = new OfflineQueueSettings
    {
        Enabled = true,
        DirectoryPath = "./iqledger-queue",
        EncryptionKey = Convert.ToBase64String(
            RandomNumberGenerator.GetBytes(32)
        )
    }
};`;

  return (
    <div className="docs-page">
      <nav className={`docs-sidebar ${navExpanded ? 'expanded' : ''}`}>
        <button 
          className="docs-nav-toggle"
          onClick={() => setNavExpanded(!navExpanded)}
          aria-expanded={navExpanded}
          aria-label="Toggle navigation"
        >
          <span className="toggle-label">Navigation</span>
          <span className="toggle-icon">{navExpanded ? '−' : '+'}</span>
        </button>
        <div className="docs-nav-content">
          <h3>Documentation</h3>
          <ul>
            <li className={activeSection === 'quickstart' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('quickstart'); setNavExpanded(false); }}>
                Quick Start
              </button>
            </li>
            <li className={activeSection === 'installation' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('installation'); setNavExpanded(false); }}>
                Installation
              </button>
            </li>
            <li className={activeSection === 'authentication' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('authentication'); setNavExpanded(false); }}>
                Authentication
              </button>
            </li>
            <li className={activeSection === 'sdk' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('sdk'); setNavExpanded(false); }}>
                SDK Reference
              </button>
            </li>
            <li className={activeSection === 'api' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('api'); setNavExpanded(false); }}>
                API Reference
              </button>
            </li>
            <li className={activeSection === 'redaction' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('redaction'); setNavExpanded(false); }}>
                Redaction Rules
              </button>
            </li>
            <li className={activeSection === 'compliance' ? 'active' : ''}>
              <button onClick={() => { setActiveSection('compliance'); setNavExpanded(false); }}>
                Compliance
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <main className="docs-content">
        <div className="docs-card">
        {activeSection === 'quickstart' && (
          <section>
            <h1>Quick Start</h1>
            <p className="docs-lead">Get logging in under 5 minutes.</p>

            <div className="docs-steps">
              <div className="docs-step">
                <span className="step-number">1</span>
                <div>
                  <h3>Create an Organization</h3>
                  <p>Sign up and create your organization from the <Link to="/signup-choice">signup page</Link>.</p>
                </div>
              </div>

              <div className="docs-step">
                <span className="step-number">2</span>
                <div>
                  <h3>Generate an API Key</h3>
                  <p>Navigate to <Link to="/profile">Profile</Link> &rarr; <strong>Generate API Key</strong>. Copy the key immediately - it's only shown once.</p>
                </div>
              </div>

              <div className="docs-step">
                <span className="step-number">3</span>
                <div>
                  <h3>Install the SDK</h3>
                  <div className="code-block">
                    <code>dotnet add package IQLedger.Sdk</code>
                    <button className="copy-btn" onClick={() => copyCode('dotnet add package IQLedger.Sdk', 'install-sdk')}>
                      {copiedId === 'install-sdk' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="docs-step">
                <span className="step-number">4</span>
                <div>
                  <h3>Start Logging</h3>
                  <div className="code-block code-block-large">
                    <pre>{quickStartCode}</pre>
                    <button className="copy-btn" onClick={() => copyCode(quickStartCode, 'quickstart-code')}>
                      {copiedId === 'quickstart-code' ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <p className="docs-note">That's it! Your logs will appear in the <Link to="/dashboard">dashboard</Link> within seconds.</p>
          </section>
        )}

        {activeSection === 'installation' && (
          <section>
            <h1>Installation</h1>
            <p className="docs-lead">Install the IQLedger SDK for .NET.</p>

            <h2>.NET CLI</h2>
            <div className="code-block">
              <code>dotnet add package IQLedger.Sdk</code>
            </div>

            <h2>Package Manager Console</h2>
            <div className="code-block">
              <code>Install-Package IQLedger.Sdk</code>
            </div>

            <h2>Requirements</h2>
            <ul className="docs-list">
              <li>.NET 8.0 or later</li>
              <li>HTTPS connectivity to <code>api.iqledger.io</code></li>
            </ul>
          </section>
        )}

        {activeSection === 'authentication' && (
          <section>
            <h1>Authentication</h1>
            <p className="docs-lead">Authenticate your SDK with an API key.</p>

            <h2>API Keys</h2>
            <p>Generate an API key from your <Link to="/profile">Profile page</Link>. The key is in the format <code>keyId.secret</code> and the SDK will automatically sign requests for you.</p>
            <div className="code-block code-block-large">
              <pre>{`var client = new IQLedgerClient(
    apiBase: "https://api.iqledger.io",
    credential: "kv_abc123.sk_xyz789"  // Your API key (keyId.secret format)
);`}</pre>
            </div>

            <h3>Security Best Practices</h3>
            <ul className="docs-list">
              <li>Store API keys in environment variables or a secrets manager</li>
              <li>Never commit API keys to source control</li>
              <li>Rotate keys periodically via the dashboard</li>
              <li>Use separate keys for development and production</li>
            </ul>

            <h2>Multi-User Organizations</h2>
            <p>Multiple users can belong to the same organization. Each user can generate their own API key, but all keys share the same organization's data and billing.</p>
            <ul className="docs-list">
              <li><strong>Admins</strong> - Can manage users, billing, and settings</li>
              <li><strong>Viewers</strong> - Can view logs but cannot modify settings</li>
            </ul>
          </section>
        )}

        {activeSection === 'sdk' && (
          <section>
            <h1>SDK Reference</h1>
            <p className="docs-lead">Complete SDK documentation for advanced usage.</p>

            <h2>Logging Methods</h2>
            <h3><code>Log(prompt, response, skipRedaction)</code></h3>
            <p>Queue a prompt/response pair for logging. PII is automatically redacted unless <code>skipRedaction</code> is true.</p>

            <h3><code>FlushAsync()</code></h3>
            <p>Block until all queued logs are sent. <strong>Always call this before application shutdown.</strong></p>

            <h2>Batching</h2>
            <p>Enable batching for better performance with high-volume logging:</p>
            <div className="code-block code-block-large">
              <pre>{batchingCode}</pre>
            </div>

            <table className="docs-table">
              <thead>
                <tr><th>Setting</th><th>Default</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>Enabled</code></td><td>false</td><td>Enable batch mode</td></tr>
                <tr><td><code>MaxBatchSize</code></td><td>10</td><td>Maximum logs per batch</td></tr>
                <tr><td><code>FlushInterval</code></td><td>2 seconds</td><td>Time before auto-flush</td></tr>
              </tbody>
            </table>

            <h2>Offline Persistence</h2>
            <p>Enable disk-backed queuing for reliability during network outages:</p>
            <div className="code-block code-block-large">
              <pre>{offlineCode}</pre>
            </div>
            <ul className="docs-list">
              <li>Logs are redacted before persistence</li>
              <li>AES-GCM encryption when <code>EncryptionKey</code> is provided</li>
              <li>Automatic replay on startup</li>
            </ul>
          </section>
        )}

        {activeSection === 'api' && (
          <section>
            <h1>API Reference</h1>
            <p className="docs-lead">REST API endpoints for direct integration.</p>

            <h2>Base URL</h2>
            <div className="code-block">
              <code>https://api.iqledger.io</code>
            </div>

            <h2>POST /logs</h2>
            <p>Submit a single log entry.</p>
            <div className="code-block code-block-large">
              <pre>{`// Request
{
  "prompt": "User input text",
  "response": "AI response text",
  "promptTokens": 50,        // optional
  "completionTokens": 100,   // optional
  "totalTokens": 150         // optional
}

// Headers
Authorization: Bearer <api-key-jwt>
Content-Type: application/json`}</pre>
            </div>

            <h2>GET /logs</h2>
            <p>Retrieve logs for your organization.</p>
            <table className="docs-table">
              <thead>
                <tr><th>Parameter</th><th>Type</th><th>Description</th></tr>
              </thead>
              <tbody>
                <tr><td><code>from</code></td><td>ISO 8601</td><td>Start date filter</td></tr>
                <tr><td><code>to</code></td><td>ISO 8601</td><td>End date filter</td></tr>
                <tr><td><code>limit</code></td><td>integer</td><td>Max results per page (default: 100)</td></tr>
                <tr><td><code>page</code></td><td>integer</td><td>Page number (1-indexed)</td></tr>
              </tbody>
            </table>
          </section>
        )}

        {activeSection === 'redaction' && (
          <section>
            <h1>Redaction Rules</h1>
            <p className="docs-lead">IQLedger automatically redacts sensitive data before storage.</p>

            <h2>Built-in Rules</h2>
            <p>These are always active and cannot be disabled:</p>
            <table className="docs-table">
              <thead>
                <tr><th>Type</th><th>Replacement</th></tr>
              </thead>
              <tbody>
                <tr><td>Social Security Numbers</td><td><code>[REDACTED_SSN]</code></td></tr>
                <tr><td>Credit Card Numbers</td><td><code>[REDACTED_CREDIT_CARD]</code></td></tr>
                <tr><td>Email Addresses</td><td><code>[REDACTED_EMAIL]</code></td></tr>
                <tr><td>Phone Numbers (10-digit)</td><td><code>[REDACTED_PHONE]</code></td></tr>
              </tbody>
            </table>

            <h2>Custom Rules</h2>
            <p>Add custom redaction patterns via the <Link to="/redaction-rules">Redaction Rules</Link> page:</p>
            <ol className="docs-list">
              <li>Click <strong>Add Rule</strong></li>
              <li>Enter a JavaScript-style regex in <strong>Pattern</strong></li>
              <li>Specify the <strong>Replacement</strong> text</li>
              <li>Save</li>
            </ol>

            <h3>Example Patterns</h3>
            <table className="docs-table">
              <thead>
                <tr><th>Use Case</th><th>Pattern</th><th>Replacement</th></tr>
              </thead>
              <tbody>
                <tr><td>API Keys</td><td><code>{`\\b[A-Za-z0-9]{32,}\\b`}</code></td><td><code>[REDACTED_KEY]</code></td></tr>
                <tr><td>Hex Hashes</td><td><code>{`\\b[A-F0-9]{64}\\b`}</code></td><td><code>[REDACTED_HASH]</code></td></tr>
                <tr><td>Keywords</td><td><code>(?i)secret|token|password</code></td><td><code>[REDACTED]</code></td></tr>
              </tbody>
            </table>
          </section>
        )}

        {activeSection === 'compliance' && (
          <section>
            <h1>Compliance & Retention</h1>
            <p className="docs-lead">Built for GDPR, SOC 2, and EU AI Act compliance.</p>

            <h2>Data Retention</h2>
            <table className="docs-table">
              <thead>
                <tr><th>Plan</th><th>Retention</th></tr>
              </thead>
              <tbody>
                <tr><td>Free</td><td>30 days</td></tr>
                <tr><td>Pro</td><td>90 days</td></tr>
                <tr><td>Enterprise</td><td>365 days</td></tr>
              </tbody>
            </table>
            <p>Data is automatically purged after the retention period.</p>

            <h2>GDPR Compliance</h2>
            <ul className="docs-list">
              <li><strong>Automatic PII Redaction:</strong> Sensitive data is redacted before storage</li>
              <li><strong>Data Access:</strong> Export all logs via API or dashboard</li>
              <li><strong>Data Deletion:</strong> Delete your organization to remove all data</li>
            </ul>

            <h2>SOC 2 & EU AI Act</h2>
            <p>IQLedger's audit trail supports:</p>
            <ul className="docs-list">
              <li><strong>Logging:</strong> Complete record of AI system inputs/outputs</li>
              <li><strong>Redaction:</strong> Proof that PII is handled appropriately</li>
              <li><strong>Retention:</strong> Configurable data lifecycle policies</li>
            </ul>
          </section>
        )}
        </div>
      </main>
    </div>
  );
}

export default Docs;
